import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, type OnDestroy, type OnInit } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import type { LogoConfig } from '../../models/ui.model';
import { ConfigResourceService } from '../../services/config-resource.service';
import { CmsApprovalCodeComponent } from '../../shared/cms-approval-code/cms-approval-code.component';
import { CopyrightComponent } from '../../shared/copyright/copyright.component';
import { LogoComponent } from '../../shared/logo/logo.component';
import { SiteDisclaimerComponent } from '../../shared/site-disclaimer/site-disclaimer.component';

@Component({
  selector: 'app-resource-template',
  templateUrl: './resource-template.component.html',
  styleUrls: ['./resource-template.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, MatDialogModule, SiteDisclaimerComponent, CopyrightComponent, CmsApprovalCodeComponent, LogoComponent],
  host: { '[class.sticky-footer-enabled]': 'footerStickyEnabled' }
})
export class ResourceTemplateComponent implements OnInit, OnDestroy {
  @Input() contentPadding: boolean = true;
  @Input() headerMarginBottom: boolean = true;
  @Input() footerStickyEnabled: boolean = true;

  clientName: string = '';
  logoUrl: string = '';
  logoAlterativeText: string = '';
  secondaryLogoUrl: string = '';
  secondaryLogoAlternativeText: string = '';
  showCopyright: boolean = true;
  footerCMSEnabled: boolean = false;
  showLastUpdated: boolean = false;
  copyright: string = '';
  lastUpdatedLabel: string = '';
  cmsApprovalCode: string = '';
  headerFooterDisclaimerText: string = '';
  contentServerUrl: string = '';
  footerLogoProperties: LogoConfig | undefined;
  footerLogoAlternateText: string = '';
  private readonly subscriptions: Subscription = new Subscription();

  constructor(private readonly configService: ConfigResourceService, private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.configService.configLoaded$.subscribe({
        next: loaded => {
          if (loaded) {
            this.clientName = this.configService.getProcessedResourceValue<string>('ClientName', '');
            this.logoUrl = this.configService.getProcessedResourceValue<string>('SiteConfig_LogoImage', '');
            this.logoAlterativeText = this.configService.getProcessedResourceValue<string>('MDCPrimaryLogoAltText', 'Company Logo');
            this.secondaryLogoUrl = this.configService.getProcessedResourceValue<string>('SiteConfig_LogoImageSecond', '');
            this.secondaryLogoAlternativeText = this.configService.getProcessedResourceValue<string>(
              'MDCSecondaryLogoAltText',
              'Partner Company Logo'
            );
            this.showCopyright = this.configService.getProcessedResourceValue<boolean>('Footer_ShowCopyright', true);
            this.copyright = this.configService.getProcessedResourceValue<string>('Spa_Site_Copyright', '');
            this.headerFooterDisclaimerText = this.configService.getProcessedResourceValue<string>('Spa_HeaderFooter_Disclaimer', '');
            this.footerCMSEnabled = this.configService.getProcessedResourceValue<boolean>('Spa_Site_FooterCMS_Enabled', true);
            this.cmsApprovalCode = this.configService.getProcessedResourceValue<string>(
              'Spa_HeaderFooter_CmsApprovalCode',
              'CMS Pending Approval'
            );
            this.showLastUpdated = this.configService.getProcessedResourceValue<boolean>('Footer_ShowLastUpdated', true);
            this.lastUpdatedLabel = this.configService.getProcessedResourceValue<string>(
              'Spa_Site_LastUpdatedLabel',
              "Last updated: {{date.format([[LastUpdated]], 'MM/DD/YYYY hh:mm A')}}"
            );
            this.contentServerUrl = this.configService.getProcessedResourceValue<string>(
              'ContentServerUrl',
              'https://contentserver.destinationrx.com/ContentServer/DRxProductContent'
            );
            this.footerLogoAlternateText = this.configService.getProcessedResourceValue<string>(
              'Spa_HeaderFooter_Logo_Alternate_Text',
              'Powered by ConnectureDRX'
            );

            const siteLogoProperties = this.configService.getProcessedResourceValue<LogoConfig>('Spa_Site_LogoProperties', {
              size: 'large',
              theme: 'color'
            });
            this.footerLogoProperties = {
              theme: siteLogoProperties?.theme?.toLowerCase() ?? '',
              size: siteLogoProperties?.size?.toLowerCase() ?? ''
            };
            this.cdr.markForCheck();
          }
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
